<template>
    <UIGridRow class="dropdown m-auto p-0" tabindex="0" @blur="onBlur">
        <UIGridCol class="dropdown-button p-0 d-flex flex-row" cols="12">
            <UIButton
                class="button w-100 h-100 d-flex flex-row justify-content-center align-items-center"
                @click="quickCheckout()"
            >
                <nuxt-resource
                    :src="`images/payment/${getSelectedPayments.id}.svg`"
                    :alt="getSelectedPayments.description"
                    :height="25"
                    :width="25"
                />
                <span class="ml-2 truncate-text">{{
                    translate('Checkout', { value: getSelectedPayments.description })
                }}</span>
            </UIButton>
            <div
                :class="{ open: open == true }"
                class="dropdown-content border d-flex align-items-center justify-content-center"
                @click.prevent="openDropdown"
            >
                <UIIcon name="chevron-down" class="dropdown-chevron"></UIIcon>
            </div>
        </UIGridCol>
        <div v-if="open" class="dropdown-item-wrapper">
            <div
                v-for="items in getMethodsMax"
                ref="dropdownItem"
                :key="items.id"
                class="dropdown-item d-flex"
                @click="setSelectPayment(items)"
            >
                <nuxt-resource
                    :src="`images/payment/${items.id}.svg`"
                    lazy
                    :alt="items.description"
                    :height="25"
                    :width="25"
                />
                <span class="ml-2">{{ items.description }}</span>
            </div>
        </div>
    </UIGridRow>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { UIIcon, UIGridRow, UIGridCol, UIButton } from '@dundle/ui/components';
import TranslationMixin from '~/mixins/TranslationMixin';
import SecurityMixin from '~/mixins/SecurityMixin';

export default {
    components: {
        UIIcon,
        UIGridRow,
        UIGridCol,
        UIButton,
    },
    mixins: [TranslationMixin, SecurityMixin],

    props: {
        methods: {
            type: Array,
        },
        product: {
            type: Object,
        },
    },

    data() {
        return {
            open: false,
            selectedPayment: null,
        };
    },

    computed: {
        ...mapGetters({
            getAvailablePaymentMethods: 'cart/getAvailablePaymentMethods',
        }),

        getMethodsMax() {
            const items = this.getAvailablePaymentMethods(this.product);
            return items.slice(0, 5);
        },

        getSelectedPayments() {
            return this.selectedPayment;
        },

        allowsCrossBorder() {
            return this.$brand.allowsCrossBorder(this.product.brand.slug);
        },
    },

    created() {
        this.selectedPayment = this.getMethodsMax[0];
    },

    methods: {
        ...mapActions({
            showModal: 'ui/showModal',
            addProductInCart: 'cart/addItem',
        }),

        openDropdown() {
            this.open = !this.open;
        },

        onBlur() {
            this.open = false;
        },

        setSelectPayment(items) {
            this.selectedPayment = items;
            this.open = !this.open;
        },

        quickCheckout() {
            if (!this.isMicrosoftApproved) {
                return this.showModal({ name: 'product-cross-border', data: { currency: this.product.currencyCode } });
            }
            if (this.isPaysafecardExperiment && !this.locatedInCorrectRegion) {
                return this.showModal({ name: 'product-cross-border', data: { region: this.product.regionCode } });
            }
            if (this.isBrandBlockedInCountry) {
                return this.showModal({
                    name: 'info',
                    data: {
                        title: this.translate('notavailableincountry.title'),
                        message: this.translate('notavailableincountry.message'),
                        button: this.translate('button.okconfirm'),
                    },
                });
            }
            if (this.showMultipleBrandsBlockedModal) {
                return this.showModal({
                    name: 'info',
                    data: {
                        title: this.translate('multipleBrands.title'),
                        message: this.translate('multipleBrands.message'),
                        button: this.translate('button.showCart'),
                    },
                });
            }

            this.open = false;
            // push quick checkout button to gtm on the product page
            this.$gtm?.push({
                event: 'event_product_page_button_quick_checkout_open',
                value: this.product.productId,
            });

            this.$analytics?.startCheckout([{ product: this.product, quantity: 1 }]);
            this.$analytics?.addPaymentInfo(this.selectedPayment.description, [this.product]);

            this.addProductInCart(this.product);
            this.showModal({ name: 'quick-checkout', data: this.getSelectedPayments, product: this.product });
        },
    },
};
</script>

<style lang="scss" scoped>
.truncate-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.dropdown {
    position: relative;
    min-height: 3rem;
    outline: none;
    .dropdown-button {
        .button {
            border: 1px solid $color-grey-3;
            background: linear-gradient(#ffffff, #eff2f4);
            color: #007cd3;
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right: 0;
        }
    }
    .dropdown-content {
        background: linear-gradient(#ffffff, #eff2f4);
        width: 3rem;
        border-top-right-radius: 3px;
        border-bottom-right-radius: 3px;
        &:hover {
            cursor: pointer;
        }
        .dropdown-chevron {
            width: 18px;
            height: 18px;
            color: $color-grey-6;
            transition: transform 0.2s;
            transform-style: preserve-3d;
        }
    }
    .open {
        .x-icon {
            transform: rotateX(-180deg);
        }
    }
    .dropdown-item-wrapper {
        position: absolute;
        top: 3.5rem;
        max-height: 15rem;
        user-select: none;
        z-index: 5;
        left: -1px;
        right: -1px;
        box-shadow: 0px 4px 10px 2px rgba(0, 0, 0, 0.3);

        .dropdown-item {
            min-height: 43px;
            padding: 0.5rem 1rem;
            line-height: 1.75;
            background: $color-form-bg;
            min-height: 3rem;
            &:hover {
                background-color: $color-grey-1;
                cursor: pointer;
            }
            .transactionCosts {
                color: $color-validation;
            }
        }

        &::-webkit-scrollbar-track {
            border-radius: 3.5px;
            background-color: $color-form-bg;
        }

        &::-webkit-scrollbar {
            width: 7px;
            border-radius: 4px;
            background-color: $color-form-bg;
        }

        &::-webkit-scrollbar-thumb {
            border-radius: 3.5px;
            background-color: $color-grey-3;
        }
    }
}
</style>

<i18n src="@dundle/locale/data/warnings.json"></i18n>
<i18n src="@dundle/locale/data/buttons.json"></i18n>
<i18n src="@dundle/locale/data/notavailable.json"></i18n>
<i18n></i18n>
